import type { RangeValue } from '@/types/filters.types';
import { useCallback } from 'react';

interface FilterRangeProps {
  value: RangeValue;
  min?: number;
  max?: number;
  onChange: (v: RangeValue) => void;
  resetLabel?: string;
}

export function FilterRange({
  value,
  min = 0,
  max = 120,
  onChange,
  resetLabel = 'ALL AGES',
}: FilterRangeProps) {
  const isDefault = value.min === min && value.max === max;

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Math.min(Number(e.target.value), value.max);
      onChange({ min: v, max: value.max });
    },
    [value.max, onChange],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Math.max(Number(e.target.value), value.min);
      onChange({ min: value.min, max: v });
    },
    [value.min, onChange],
  );

  const leftPct = ((value.min - min) / (max - min)) * 100;
  const rightPct = ((value.max - min) / (max - min)) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">
          {value.min} - {value.max}
        </span>
        {!isDefault && (
          <button
            onClick={() => onChange({ min, max })}
            className="text-[10px] text-primary hover:underline"
          >
            {resetLabel}
          </button>
        )}
      </div>
      <div className="relative h-5">
        <div className="absolute top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 bg-blue-600 rounded-full"
          style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value.min}
          onChange={handleMinChange}
          className="absolute w-full top-0 h-5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:cursor-pointer"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value.max}
          onChange={handleMaxChange}
          className="absolute w-full top-0 h-5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:cursor-pointer"
        />
      </div>
    </div>
  );
}
